import React, { useCallback, useEffect, useState } from "react";
import {
  createGrade,
  deleteGrade,
  listGrades,
  updateGrade,
} from "../../../../../../services/gradeService";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../../../../../helpers/methods";
import YhButton from "../../../../../../ui/YhButton";
import YhInput from "../../../../../../ui/YhInput";
import YhModal from "../../../../../../ui/YhModal";

const GradeList: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listGrades({ all: "1" });
      const data = res?.data || res || [];
      setItems(Array.isArray(data) ? data : []);
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to load grades");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditId("");
    setName("");
    setDescription("");
    setOpen(true);
  };

  const openEdit = (row: any) => {
    setEditId(String(row._id));
    setName(row.name || "");
    setDescription(row.description || "");
    setOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      openErrorNotification("Grade name is required");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await updateGrade(editId, {
          name: name.trim(),
          description: description.trim(),
        });
        openSuccessNotification("Grade updated");
      } else {
        await createGrade({
          name: name.trim(),
          description: description.trim(),
        });
        openSuccessNotification("Grade created");
      }
      setOpen(false);
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: any) => {
    if (!window.confirm(`Remove grade "${row.name}"?`)) return;
    try {
      await deleteGrade(String(row._id));
      openSuccessNotification("Grade removed");
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Delete failed");
    }
  };

  const toggleStatus = async (row: any) => {
    try {
      await updateGrade(String(row._id), {
        status: row.status === "active" ? "inactive" : "active",
      });
      load();
    } catch (e: any) {
      openErrorNotification(e?.message || "Update failed");
    }
  };

  return (
    <div className="box-border w-full space-y-5 px-4 py-4 md:px-6 md:py-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#111827]">
            Channel Partner Grades
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Grade name and optional description. Defaults Grade A and Grade B
            are created automatically. Selectable when adding Channel Partners.
          </p>
        </div>
        <YhButton variant="primary" size="sm" onClick={openCreate}>
          Add Grade
        </YhButton>
      </div>

      <div className="w-full overflow-x-auto rounded-xl border border-[#E5E7EB] bg-white">
        <table className="w-full min-w-[640px] text-left text-sm">
          <thead className="bg-[#F9FAFB] text-[#6B7280]">
            <tr>
              <th className="px-4 py-3 font-medium">Grade name</th>
              <th className="px-4 py-3 font-medium">Description</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Default</th>
              <th className="px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td className="px-4 py-8 text-[#6B7280]" colSpan={5}>
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td className="px-4 py-8 text-[#6B7280]" colSpan={5}>
                  No grades yet.
                </td>
              </tr>
            ) : (
              items.map((row) => (
                <tr key={row._id} className="border-t border-[#F3F4F6]">
                  <td className="px-4 py-3 font-medium text-[#111827]">
                    {row.name}
                  </td>
                  <td className="px-4 py-3 text-[#6B7280]">
                    {row.description || "—"}
                  </td>
                  <td className="px-4 py-3 capitalize">{row.status}</td>
                  <td className="px-4 py-3">{row.isDefault ? "Yes" : "—"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <YhButton
                        variant="secondary"
                        size="sm"
                        onClick={() => openEdit(row)}
                      >
                        Edit
                      </YhButton>
                      <YhButton
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleStatus(row)}
                      >
                        {row.status === "active" ? "Deactivate" : "Activate"}
                      </YhButton>
                      {!row.isDefault && (
                        <YhButton
                          variant="danger"
                          size="sm"
                          onClick={() => handleDelete(row)}
                        >
                          Delete
                        </YhButton>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <YhModal
        open={open}
        onClose={() => !saving && setOpen(false)}
        title={editId ? "Edit Grade" : "Add Grade"}
        width={440}
        footer={
          <div className="flex justify-end gap-2">
            <YhButton
              variant="secondary"
              size="sm"
              disabled={saving}
              onClick={() => setOpen(false)}
            >
              Cancel
            </YhButton>
            <YhButton
              variant="primary"
              size="sm"
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? "Saving…" : "Save"}
            </YhButton>
          </div>
        }
      >
        <div className="space-y-3">
          <YhInput
            label="Grade name *"
            value={name}
            onChange={(e: any) => setName(e.target?.value ?? e)}
            placeholder="e.g. Grade C"
          />
          <YhInput
            label="Description (optional)"
            value={description}
            onChange={(e: any) => setDescription(e.target?.value ?? e)}
            placeholder="Short description"
          />
        </div>
      </YhModal>
    </div>
  );
};

export default GradeList;
